import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { apiRequest, API_BASE_URL } from "../../services/apiClient";
import { useLanguage } from "../../i18n/LanguageContext";

function getVerifyToken() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return String(params.get("token") || params.get("verifyToken") || "").trim();
  } catch {
    return "";
  }
}

export default function VerifyEmailPage({ token: tokenProp, onContinue, onVerified }) {
  const { t } = useLanguage();
  const token = useMemo(() => String(tokenProp || getVerifyToken() || "").trim(), [tokenProp]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const onVerifiedRef = useRef(onVerified);

  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  const continueVerifiedSession = useCallback(async () => {
    try {
      return (await onVerifiedRef.current?.()) === true;
    } catch {
      // E-posta backend'de doğrulandı. Sonraki oturum/aile yenilemesi geçici
      // olarak başarısız olsa bile doğrulama sonucunu "geçersiz"e çevirmeyiz.
      return false;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const confirm = async () => {
      setLoading(true);
      setError("");
      try {
        if (!token || token.length < 32) throw new Error(t("auth.verifyEmailLinkInvalid"));
        await apiRequest("/auth/verify-email", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        if (!alive) return;
        setSuccess(true);
        try { window.history.replaceState({}, "", "/"); } catch {}
        await continueVerifiedSession();
      } catch (err) {
        if (!alive) return;
        setSuccess(false);
        setError(err?.message || t("auth.verifyEmailLinkInvalid"));
      } finally {
        if (alive) setLoading(false);
      }
    };
    confirm();
    return () => { alive = false; };
  }, [continueVerifiedSession, token, t]);

  const goApp = async () => {
    try { window.history.replaceState({}, "", "/"); } catch {}
    const continued = await continueVerifiedSession();
    if (continued) return;
    if (Capacitor.isNativePlatform() && typeof onContinue === "function") {
      onContinue();
      return;
    }
    // Web ekranındayken farklı hosttaki doğrulanmış Universal/App Link'e git.
    // Uygulama yüklüyse native uygulama açılır; değilse backend web'e döner.
    window.location.assign(`${API_BASE_URL}/auth/deep-link/open`);
  };

  return (
    <div className="authPage2 resetPasswordContainer">
      <div className="authScreen resetPasswordScreen">
        <div className="authHero resetPasswordHero">
          <div className="authHeroLogo">
            <img src="/logo512.png" alt={t("app.name")} className="authHeroLogoImage" />
          </div>
          <h1 className="authHeroTitle">{t("auth.verifyEmail")}</h1>
        </div>

        <div className="authFormCard resetPasswordCard">
          <div className="authFormCardGlow" />

          {loading ? (
            <div className="resetPasswordStateBox">
              <div className="resetPasswordSpinner" />
              <h2>{t("auth.verifyEmailChecking")}</h2>
              <p>{t("auth.verifyEmailCheckingSub")}</p>
            </div>
          ) : success ? (
            <div className="resetPasswordStateBox">
              <div className="resetPasswordSuccessIcon">✓</div>
              <h2>{t("auth.info.emailVerified")}</h2>
              <p>{t("auth.verifyEmailSuccessSub")}</p>
              <button type="button" className="authLoginBtn" onClick={goApp}>{t("auth.backToLogin")}</button>
            </div>
          ) : (
            <div className="resetPasswordStateBox">
              <div className="resetPasswordErrorIcon">!</div>
              <h2>{t("auth.verifyEmailInvalidTitle")}</h2>
              <p>{error || t("auth.verifyEmailLinkInvalid")}</p>
              <button type="button" className="authLoginBtn" onClick={goApp}>{t("auth.backToLogin")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
